import DeckGL from '@deck.gl/react';
import BatchTracing from 'components/Shared/BatchTracingContainer/BatchTracing';
import useMapViewport, { MAP_ANIMATION, } from 'components/Shared/ProductJourney/hooks/useMapViewport';
import { useBrowser, useIsIntersecting } from 'hooks';
import React, { memo, useMemo, useState } from 'react';
import { AttributionControl, _MapContext as MapContext, NavigationControl, StaticMap, } from 'react-map-gl';
import styled from 'styled-components';
import { PinTarget } from 'types/types';
import { getMapDisplayMode } from 'utils/mapUtils';
import { useActiveContext } from '../Context/ActiveContext';
import StepNavigation from '../Slides/StepNavigation';
import MarkersTransition from './Marker/MarkersTransition';
const LINK_THRESHOLD = 4;
const StyledNavigationControl = styled(NavigationControl) `
  bottom: 40px;
  right: 16px;
`;
const StyledAttributionControl = styled(AttributionControl) `
  font-size: 10px;
  bottom: ${({ isMobile }) => (isMobile ? 30 : 5)}px;
  right: ${({ isMobile }) => (isMobile ? 4 : 19)}px;
  margin: 0;

  .mapboxgl-ctrl-attrib.mapboxgl-compact {
    margin: 0;
    display: ${({ isSummaryMap }) => (isSummaryMap ? 'none' : 'block')};

    &::after {
      bottom: 0;
      right: 0;
      margin: 0;
    }
  }
`;
const NavigationContainer = styled.div `
  position: absolute;
  top: ${({ isMobile }) => (isMobile ? 48 : 16)}px;
  left: ${({ isMobile }) => (isMobile ? 12 : 32)}px;
`;
const MapButtonContainer = styled.div `
  position: absolute;
  z-index: 1000;
  bottom: 8px;
  left: 8px;
`;
const MapContainer = styled.div `
  .mapboxgl-ctrl-bottom-right {
    margin-right: 42px;
  }
`;
const AnimatedMap = ({ height = '100%', width, isSummaryMap, }) => {
    const { isMobile } = useBrowser();
    const { steps, batchTracingPossible, activeStep, activeStepIndex, allStepActivityLinks, selectedRawMaterialId, isBatchTracingActive, allRawMaterials, activeStepActivity, hasSelectedStepActivityOtherIngredients, isSelectedActivityTraceable, isSelectedStepTraceable, handleStepChange, handleTraceMyBatchClick, handleSelectedActivityChange, handleAnimatedPinChange, handleStopBatchTracingClick, handleGoToBatchDataStep, } = useActiveContext();
    const mapType = getMapDisplayMode(allStepActivityLinks, LINK_THRESHOLD);
    const selectedRawMaterial = useMemo(() => allRawMaterials.find((rm) => rm.id === selectedRawMaterialId), [selectedRawMaterialId, allRawMaterials]);
    const { isIntersecting, intersectionRef } = useIsIntersecting();
    const [mapLoaded, setMapLoaded] = useState(false);
    // If the amount of links ( lines on the map ) is higher than the threshold, we change the maptype
    const { viewport, layers, isCenterSameAsPrevious, isCenterNearbyPrevious } = useMapViewport({
        width,
        isSummaryMap,
        isIntersecting,
        mapLoaded,
        mapType,
    });
    // Each step that will be rendered on the map with a marker.
    const stepsMarkedOnMap = useMemo(() => {
        if (isSummaryMap) {
            // In the summary map all steps should be visible, hence we can return them all.
            return steps;
        }
        return steps.slice(0, activeStepIndex + 1);
    }, [activeStepIndex, steps, isSummaryMap, selectedRawMaterialId]);
    // Accumulates all the links so the markerstransition can look through all the possible source / destinations
    // to determine which nodes to show ( markers )
    const allStepLinks = useMemo(() => stepsMarkedOnMap.flatMap((step) => step.chainStepActivities.flatMap(({ incomingLinks }) => incomingLinks)), [stepsMarkedOnMap]);
    const onMarkerClick = (id) => {
        if (isSummaryMap) {
            return;
        }
        handleSelectedActivityChange(id);
        handleAnimatedPinChange({ id: id, target: PinTarget.NODE });
    };
    if (!steps.length) {
        return (React.createElement(MapContainer, null,
            React.createElement(StaticMap, { width: "100%", height: height, mapStyle: "mapbox://styles/seedtrace/clia0bhp5004l01pghikh13gg", mapboxApiAccessToken: process.env.NEXT_MAPBOX_API_TOKEN, attributionControl: false, mapOptions: {
                    logoPosition: isMobile ? 'bottom-left' : 'bottom-right',
                } })));
    }
    return (React.createElement("div", { ref: intersectionRef, style: { paddingLeft: '300px' } },
        React.createElement(MapContainer, null,
            React.createElement(DeckGL
            // @ts-ignore
            , { 
                // @ts-ignore
                ContextProvider: MapContext.Provider, initialViewState: viewport, controller: {
                    scrollZoom: true,
                    dragRotate: false,
                    doubleClickZoom: false,
                    touchZoom: true,
                    touchRotate: false,
                }, layers: layers },
                React.createElement(StyledNavigationControl, { className: "st-desktop-only", showCompass: false }),
                React.createElement(StyledAttributionControl, { compact: true, isMobile: isMobile, isSummaryMap: isSummaryMap }),
                React.createElement(StaticMap, { width: "100%", height: height, mapStyle: "mapbox://styles/seedtrace/clia0bhp5004l01pghikh13gg", mapboxApiAccessToken: process.env.NEXT_MAPBOX_API_TOKEN, onLoad: () => setMapLoaded(true), attributionControl: false, mapOptions: {
                        logoPosition: isMobile ? 'bottom-left' : 'bottom-right',
                    } }),
                !isSummaryMap && (React.createElement(NavigationContainer, { isMobile: isMobile },
                    React.createElement(StepNavigation, { width: width - (isMobile ? 24 : 64), count: steps.length, activeIndex: activeStepIndex, onChange: handleStepChange, activeStepTitle: activeStep === null || activeStep === void 0 ? void 0 : activeStep.title, backButton: true }))),
                batchTracingPossible && !isSummaryMap && !isMobile && (React.createElement(MapButtonContainer, null,
                    React.createElement(BatchTracing, { hasOtherIngredients: hasSelectedStepActivityOtherIngredients, rawMaterial: selectedRawMaterial, batchTracingActive: isBatchTracingActive, chainStepActivity: activeStepActivity, notTraceableActivity: !isSelectedActivityTraceable, notTraceableStep: !isSelectedStepTraceable, onGoToFirstBatchDataStep: handleGoToBatchDataStep, onBatchTracingStart: handleTraceMyBatchClick, onBatchTracingEnd: handleStopBatchTracingClick }))),
                stepsMarkedOnMap.map(({ id, chainStepActivities }, index) => (React.createElement(MarkersTransition, { key: id, mapType: mapType, activities: chainStepActivities, linksSerialized: allStepLinks, onMarkerClick: onMarkerClick, delay: activeStepIndex === 0 || isCenterSameAsPrevious
                        ? 0
                        : isCenterNearbyPrevious
                            ? MAP_ANIMATION.SHORT - 500
                            : MAP_ANIMATION.LONG - 500, forStepIndex: index })))))));
};
export default memo(AnimatedMap);
